import React, { useState, useEffect } from 'react';
import {
    View, Text, StyleSheet, TouchableOpacity, Image,
    Dimensions, Modal, Pressable, Alert, ActivityIndicator, ScrollView
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Video } from 'expo-av';
import UserAvatar from '../shared/UserAvatar';
import ZoomableImage from '../shared/ZoomableImage';
import PostActions from './PostActions';
import ShortClipPlayer from './ShortClipPlayer';
import PhotoClipPlayer from './PhotoClipPlayer';
import { getMediaUrl } from '../../../utils/media';
import { useAuth } from '../../../contexts/AuthContext';
import { deletePost, reportPost } from '../../../api/social';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const CONTENT_WIDTH = SCREEN_WIDTH - (14 * 2) - 38 - 8;
const MAX_IMG_HEIGHT = Math.round(SCREEN_HEIGHT * 0.65);
const MIN_IMG_HEIGHT = 160;

const REPORT_REASONS = [
    'Spam or misleading',
    'Hate speech or harassment',
    'Violence or harmful content',
    'Nudity or sexual content',
    'Misinformation',
    'Other',
];

// Resolve display handle: prefer socialHandle, fall back to derived @name
const toHandle = (user) => {
    if (user?.socialHandle) return '@' + user.socialHandle;
    if (!user?.name) return '@user';
    return '@' + user.name.toLowerCase().replace(/\s+/g, '');
};

const PostCard = React.memo(({ post, onLike, onComment, onShare, onProfilePress, isLiked, onPostDeleted, showGrid = true, isVisible = true }) => {
    const { user: currentUser } = useAuth();
    const [menuVisible, setMenuVisible] = useState(false);
    const [reportSheetVisible, setReportSheetVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReporting, setIsReporting] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [imgHeight, setImgHeight] = useState(Math.round(CONTENT_WIDTH * 9 / 16));

    const isOwner = post.user?.id === currentUser?.id;

    useEffect(() => {
        let urls = [];
        if (post.mediaUrl) {
            try { urls = post.mediaUrl.startsWith('[') ? JSON.parse(post.mediaUrl) : [post.mediaUrl]; }
            catch { urls = [post.mediaUrl]; }
        }
        
        // For single items OR photo clips, we try to match the height of the first media item
        if (urls.length === 0) return;

        const resolved = getMediaUrl(urls[0]);
        const isVideo = post.mediaType?.startsWith('video/') || post.type === 'video';
        
        if (isVideo) {
            setImgHeight(Math.min(Math.round(CONTENT_WIDTH * 1.6), MAX_IMG_HEIGHT));
            return;
        }

        if (post.type === 'photo_clip') {
            setImgHeight(Math.min(Math.round(CONTENT_WIDTH * 1.4), MAX_IMG_HEIGHT));
            return;
        }

        Image.getSize(
            resolved,
            (w, h) => {
                if (w > 0) {
                    const calc = Math.min(Math.max(Math.round((h / w) * CONTENT_WIDTH), MIN_IMG_HEIGHT), MAX_IMG_HEIGHT);
                    setImgHeight(calc);
                }
            },
            () => {} 
        );
    }, [post.mediaUrl, post.type]);

    // ── Date formatter ────────────────────────────────────────────────────────
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now  = new Date();
        const diff = Math.floor((now - date) / 1000);
        if (diff < 60)     return 'now';
        if (diff < 3600)   return `${Math.floor(diff / 60)}m`;
        if (diff < 86400)  return `${Math.floor(diff / 3600)}h`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = () => {
        setMenuVisible(false);
        Alert.alert('Delete Post', 'Are you sure you want to delete this post? This cannot be undone.', [
            { text: 'Cancel', style: 'cancel' },
            {
                text: 'Delete', style: 'destructive',
                onPress: async () => {
                    setIsDeleting(true);
                    try {
                        await deletePost(post.id);
                        onPostDeleted?.(post.id);
                    } catch {
                        Alert.alert('Error', 'Failed to delete post. Please try again.');
                    } finally {
                        setIsDeleting(false);
                    }
                }
            }
        ]);
    };

    // ── Report ────────────────────────────────────────────────────────────────
    const handleReportReason = async (reason) => {
        setReportSheetVisible(false);
        setIsReporting(true);
        try {
            await reportPost(post.id, reason);
            Alert.alert('Report Submitted', 'Thank you. Our team will review this post shortly.');
        } catch {
            Alert.alert('Error', 'Failed to submit report. Please try again.');
        } finally {
            setIsReporting(false);
        }
    };

    // ── Grid media (multi-image, unchanged layout) ────────────────────────────
    const renderGridMedia = (mediaUrls) => {
        const count = mediaUrls.length;
        const W = CONTENT_WIDTH;
        const GRID_H   = Math.round(W * (9 / 16));
        const HALF_H   = Math.round((GRID_H - 2) / 2);
        const HALF_W   = Math.round((W - 2) / 2);
        const TWO3_W   = Math.round((W - 2) * 2 / 3);
        const ONE3_W   = W - 2 - TWO3_W;

        const imgBtn = (url, w, h, extra) => (
            <TouchableOpacity
                key={url + w + h}
                onPress={() => setLightboxUrl(getMediaUrl(url))}
                activeOpacity={0.85}
                style={{ width: w, height: h, position: 'relative' }}
            >
                <Image source={{ uri: getMediaUrl(url) }} style={{ width: w, height: h }} resizeMode="cover" />
                {extra != null && extra > 0 && (
                    <View style={styles.gridOverlay}>
                        <Text style={styles.gridOverlayText}>+{extra}</Text>
                    </View>
                )}
            </TouchableOpacity>
        );

        if (count === 1) return imgBtn(mediaUrls[0], W, GRID_H);
        if (count === 2) return (
            <View style={{ flexDirection: 'row', height: GRID_H, gap: 2 }}>
                {imgBtn(mediaUrls[0], HALF_W, GRID_H)}
                {imgBtn(mediaUrls[1], HALF_W, GRID_H)}
            </View>
        );
        if (count === 3) return (
            <View style={{ flexDirection: 'row', height: GRID_H, gap: 2 }}>
                {imgBtn(mediaUrls[0], TWO3_W, GRID_H)}
                <View style={{ width: ONE3_W, gap: 2 }}>
                    {imgBtn(mediaUrls[1], ONE3_W, HALF_H)}
                    {imgBtn(mediaUrls[2], ONE3_W, HALF_H)}
                </View>
            </View>
        );
        const displayUrls = mediaUrls.slice(0, 4);
        const extra = count - 4;
        return (
            <View style={{ flexDirection: 'row', height: GRID_H, gap: 2 }}>
                <View style={{ width: HALF_W, gap: 2 }}>
                    {imgBtn(displayUrls[0], HALF_W, HALF_H)}
                    {imgBtn(displayUrls[1], HALF_W, HALF_H)}
                </View>
                <View style={{ width: HALF_W, gap: 2 }}>
                    {imgBtn(displayUrls[2], HALF_W, HALF_H)}
                    {imgBtn(displayUrls[3], HALF_W, HALF_H, extra)}
                </View>
            </View>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <View style={[styles.container, isDeleting && styles.containerDeleting]}>
            {isDeleting && (
                <View style={styles.deletingOverlay}>
                    <ActivityIndicator color="#EF4444" />
                    <Text style={styles.deletingText}>Deleting...</Text>
                </View>
            )}

            {/* Repost banner */}
            {post.type === 'repost' && (
                <View style={styles.repostIndicator}>
                    <Ionicons name="repeat" size={13} color="#536471" />
                    <Text style={styles.repostText}>Reposted</Text>
                </View>
            )}

            <View style={styles.row}>
                {/* Avatar — self-manages enlarged lightbox on tap */}
                <UserAvatar
                    source={post.user?.profileImageUrl}
                    name={post.user?.name}
                    size={38}
                />

                {/* Content column */}
                <View style={styles.contentCol}>
                    {/* Header */}
                    <View style={styles.headerRow}>
                        <TouchableOpacity
                            style={styles.authorInfo}
                            onPress={() => onProfilePress?.(post.user?.id)}
                            activeOpacity={0.75}
                        >
                            <Text style={styles.userName} numberOfLines={1}>{post.user?.name}</Text>
                            {post.user?.role === 'admin' && (
                                <View style={styles.adminBadge}>
                                    <Text style={styles.adminBadgeText}>SYSADMIN</Text>
                                </View>
                            )}
                            <Text style={styles.handle} numberOfLines={1}>{toHandle(post.user)}</Text>
                            <Text style={styles.dot}>·</Text>
                            <Text style={styles.timestamp}>{formatDate(post.createdAt)}</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.moreBtn}
                            onPress={() => setMenuVisible(true)}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                            <Ionicons name="ellipsis-horizontal" size={18} color="#536471" />
                        </TouchableOpacity>
                    </View>

                    {/* Text content */}
                    {post.content ? (
                        <TouchableOpacity onPress={() => onComment?.(post.id)} activeOpacity={onComment ? 0.75 : 1}>
                            <Text style={styles.contentText}>{post.content}</Text>
                        </TouchableOpacity>
                    ) : null}

                    {/* Media */}
                    {post.mediaUrl && (() => {
                        let mediaUrls = [];
                        let mediaThumbnails = [];
                        try {
                            mediaUrls = post.mediaUrl.startsWith('[') ? JSON.parse(post.mediaUrl) : [post.mediaUrl];
                        } catch { mediaUrls = [post.mediaUrl]; }
                        try {
                            mediaThumbnails = post.mediaThumbnail?.startsWith('[') ? JSON.parse(post.mediaThumbnail) : (post.mediaThumbnail ? [post.mediaThumbnail] : []);
                        } catch { mediaThumbnails = post.mediaThumbnail ? [post.mediaThumbnail] : []; }

                        const isMulti = mediaUrls.length > 1;
                        if (mediaUrls.length === 0) return null;

                        return (
                            <View style={[
                                styles.mediaContainer,
                                post.type === 'photo_clip' || (isMulti && showGrid)
                                    ? { height: (post.type === 'photo_clip' ? imgHeight : Math.round(CONTENT_WIDTH * 9 / 16)) }
                                    : { height: imgHeight }
                            ]}>
                                {post.type === 'photo_clip' ? (
                                    (() => {
                                        let animConfig = {};
                                        try {
                                            animConfig = typeof post.animationConfig === 'string' 
                                                ? JSON.parse(post.animationConfig) 
                                                : (post.animationConfig || {});
                                        } catch (e) { console.warn('Failed to parse animConfig', e); }

                                        const images = mediaUrls.map(u => getMediaUrl(u));
                                        
                                        return (
                                            <PhotoClipPlayer
                                                images={images}
                                                animationConfig={animConfig}
                                                width={CONTENT_WIDTH}
                                                height={imgHeight}
                                                borderRadius={12}
                                                autoPlay={isVisible}
                                            />
                                        );
                                    })()
                                ) : isMulti ? (
                                    showGrid ? renderGridMedia(mediaUrls) : (
                                        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                            {mediaUrls.map((url, index) => {
                                                const isVideo = post.mediaType?.includes('video') || post.type === 'video';
                                                
                                                return !isVideo ? (
                                                    <TouchableOpacity key={index} onPress={() => setLightboxUrl(getMediaUrl(url))} activeOpacity={0.85}>
                                                        <Image
                                                            source={{ uri: getMediaUrl(url) }}
                                                            style={styles.multiMediaImage}
                                                            resizeMode="cover"
                                                        />
                                                    </TouchableOpacity>
                                                ) : (
                                                    <ShortClipPlayer
                                                        key={index}
                                                        uri={getMediaUrl(url)}
                                                        poster={getMediaUrl(mediaThumbnails[index] || url)}
                                                        width={CONTENT_WIDTH}
                                                        height={imgHeight}
                                                        borderRadius={12}
                                                        autoPlay={isVisible}
                                                    />
                                                );
                                            })}
                                        </ScrollView>
                                    )
                                ) : (
                                    (() => {
                                        const url = mediaUrls[0];
                                        const isVideo = post.mediaType?.includes('video') || post.type === 'video';
                                        
                                        return !isVideo ? (
                                            <ZoomableImage
                                                uri={getMediaUrl(url)}
                                                width={CONTENT_WIDTH}
                                                height={imgHeight}
                                                resizeMode="cover"
                                                borderRadius={12}
                                                onPress={() => setLightboxUrl(getMediaUrl(url))}
                                                zoomEnabled={false}
                                            />
                                        ) : (
                                            <ShortClipPlayer
                                                uri={getMediaUrl(url)}
                                                poster={getMediaUrl(mediaThumbnails[0] || url)}
                                                width={CONTENT_WIDTH}
                                                height={imgHeight}
                                                borderRadius={12}
                                                autoPlay={isVisible}
                                            />
                                        );
                                    })()
                                )}
                            </View>
                        );
                    })()}

                    <PostActions
                        likes={post.likesCount}
                        comments={post.commentsCount}
                        reposts={post.repostsCount}
                        isLiked={isLiked}
                        onLike={() => onLike?.(post.id)}
                        onComment={() => onComment?.(post.id)}
                        onRepost={() => onShare?.(post.id)}
                    />
                </View>
            </View>

            {/* ── Full-screen lightbox with ZoomableImage ── */}
            <Modal
                visible={!!lightboxUrl}
                transparent
                animationType="fade"
                onRequestClose={() => setLightboxUrl(null)}
                statusBarTranslucent
            >
                <View style={styles.lightboxBackdrop}>
                    {lightboxUrl && (
                        <ZoomableImage
                            uri={lightboxUrl}
                            width={SCREEN_WIDTH}
                            height={SCREEN_HEIGHT}
                            resizeMode="contain"
                            borderRadius={0}
                            onPress={() => setLightboxUrl(null)}
                            zoomEnabled={true}
                        />
                    )}
                    <TouchableOpacity style={styles.lightboxClose} onPress={() => setLightboxUrl(null)}>
                        <Ionicons name="close" size={28} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </Modal>

            {/* ── Action Menu ── */}
            <Modal
                visible={menuVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setMenuVisible(false)}
            >
                <Pressable style={styles.modalBackdrop} onPress={() => setMenuVisible(false)}>
                    <View style={styles.menuSheet}>
                        <View style={styles.menuHandle} />

                        {isOwner ? (
                            <>

                                {/* Delete */}
                                <TouchableOpacity style={[styles.menuItem, styles.menuItemDanger]} onPress={handleDelete}>
                                    <View style={[styles.menuIconBg, { backgroundColor: '#FEE2E2' }]}>
                                        <Ionicons name="trash-outline" size={20} color="#EF4444" />
                                    </View>
                                    <View style={styles.menuTextGroup}>
                                        <Text style={[styles.menuItemLabel, { color: '#EF4444' }]}>Delete Post</Text>
                                        <Text style={styles.menuItemSub}>Permanently remove this post</Text>
                                    </View>
                                    <Ionicons name="chevron-forward" size={16} color="#EF4444" />
                                </TouchableOpacity>
                            </>
                        ) : (
                            <TouchableOpacity
                                style={styles.menuItem}
                                onPress={() => { setMenuVisible(false); setTimeout(() => setReportSheetVisible(true), 300); }}
                            >
                                <View style={[styles.menuIconBg, { backgroundColor: '#FEF3C7' }]}>
                                    <Ionicons name="flag-outline" size={20} color="#D97706" />
                                </View>
                                <View style={styles.menuTextGroup}>
                                    <Text style={styles.menuItemLabel}>Report Post</Text>
                                    <Text style={styles.menuItemSub}>Flag offensive or harmful content</Text>
                                </View>
                                <Ionicons name="chevron-forward" size={16} color="#9CA3AF" />
                            </TouchableOpacity>
                        )}

                        <TouchableOpacity
                            style={[styles.menuItem, { borderBottomWidth: 0 }]}
                            onPress={() => setMenuVisible(false)}
                        >
                            <View style={[styles.menuIconBg, { backgroundColor: '#F3F4F6' }]}>
                                <Ionicons name="close-outline" size={20} color="#6B7280" />
                            </View>
                            <Text style={styles.menuItemLabel}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                </Pressable>
            </Modal>

            {/* ── Report Sheet ── */}
            <Modal
                visible={reportSheetVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setReportSheetVisible(false)}
            >
                <Pressable style={styles.modalBackdrop} onPress={() => setReportSheetVisible(false)}>
                    <View style={styles.reportSheet}>
                        <View style={styles.menuHandle} />
                        <Text style={styles.reportTitle}>Why are you reporting this?</Text>
                        <Text style={styles.reportSubtitle}>Your report is anonymous. Help us keep the community safe.</Text>
                        {isReporting ? (
                            <View style={styles.reportingLoader}>
                                <ActivityIndicator color="#D97706" />
                                <Text style={styles.reportingText}>Submitting report...</Text>
                            </View>
                        ) : (
                            REPORT_REASONS.map((reason, index) => (
                                <TouchableOpacity
                                    key={reason}
                                    style={[styles.reasonItem, index === REPORT_REASONS.length - 1 && { borderBottomWidth: 0 }]}
                                    onPress={() => handleReportReason(reason)}
                                >
                                    <Text style={styles.reasonText}>{reason}</Text>
                                    <Ionicons name="chevron-forward" size={16} color="#9CA3AF" />
                                </TouchableOpacity>
                            ))
                        )}
                    </View>
                </Pressable>
            </Modal>

        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingTop: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E1E8ED',
    },
    containerDeleting: { opacity: 0.5 },
    deletingOverlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(255,255,255,0.8)',
        zIndex: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    deletingText: { color: '#EF4444', fontWeight: '600', fontSize: 14 },
    repostIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 4,
        paddingLeft: 46,
        gap: 5,
    },
    repostText: { fontSize: 12, color: '#536471', fontWeight: '600' },
    row: { flexDirection: 'row', gap: 8 },
    contentCol: { flex: 1 },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    authorInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        gap: 4,
        flexWrap: 'nowrap',
        overflow: 'hidden',
    },
    userName: { fontSize: 15, fontWeight: '700', color: '#0F1419', flexShrink: 0, maxWidth: 100 },
    adminBadge: {
        backgroundColor: '#06b6d4',
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 4,
        marginLeft: 2,
    },
    adminBadgeText: { color: '#FFFFFF', fontSize: 9, fontWeight: '900', letterSpacing: 0.5 },
    handle: { fontSize: 14, color: '#536471', flexShrink: 1 },
    dot: { fontSize: 14, color: '#536471' },
    timestamp: { fontSize: 14, color: '#536471', flexShrink: 0 },
    moreBtn: { padding: 4, marginLeft: 4 },
    contentText: { fontSize: 15, lineHeight: 21, color: '#0F1419', marginTop: 2 },
    mediaContainer: {
        marginTop: 10,
        borderRadius: 12,
        overflow: 'hidden',
        width: CONTENT_WIDTH,
    },
    multiMediaImage: {
        width: CONTENT_WIDTH,
        height: CONTENT_WIDTH * (9 / 16),
        marginRight: 10,
        borderRadius: 12,
    },
    gridOverlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.45)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    gridOverlayText: { color: '#FFFFFF', fontSize: 22, fontWeight: '800' },

    // Lightbox
    lightboxBackdrop: {
        flex: 1,
        backgroundColor: '#000000',
        alignItems: 'center',
        justifyContent: 'center',
    },
    lightboxClose: {
        position: 'absolute',
        top: 50,
        right: 16,
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 22,
        padding: 8,
    },

    // Modals
    modalBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.45)',
        justifyContent: 'flex-end',
    },
    menuSheet: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        paddingTop: 8,
        paddingBottom: 30,
        paddingHorizontal: 16,
    },
    menuHandle: {
        width: 40, height: 4,
        backgroundColor: '#E5E7EB',
        borderRadius: 2,
        alignSelf: 'center',
        marginBottom: 16,
    },
    menuItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
        gap: 12,
    },
    menuItemDanger: {},
    menuIconBg: {
        width: 40, height: 40,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    menuTextGroup: { flex: 1 },
    menuItemLabel: { fontSize: 15, fontWeight: '600', color: '#111827' },
    menuItemSub: { fontSize: 12, color: '#9CA3AF', marginTop: 1 },

    // Report
    reportSheet: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        paddingTop: 8,
        paddingBottom: 36,
        paddingHorizontal: 16,
    },
    reportTitle: { fontSize: 17, fontWeight: '700', color: '#111827', marginBottom: 4, marginTop: 8 },
    reportSubtitle: { fontSize: 13, color: '#6B7280', marginBottom: 14, lineHeight: 18 },
    reasonItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    reasonText: { fontSize: 15, color: '#374151', fontWeight: '500' },
    reportingLoader: {
        flexDirection: 'row', alignItems: 'center',
        justifyContent: 'center', gap: 10, paddingVertical: 24,
    },
    reportingText: { color: '#D97706', fontWeight: '600' },
});

export default PostCard;
